// Zero-Server local data layer.
// All persistent app data — scan history, error-scan history, settings — lives in
// IndexedDB on the user's device. Sensitive fields (rows, text, suggestions) are
// AES-GCM-encrypted at rest with a device-bound key managed via the Web Crypto
// API. The key never leaves the browser; even if the underlying IndexedDB blob
// is dumped, content is unreadable without it.
//
// We intentionally keep `title`, `timestamp` and `kind` as plaintext so the
// history sidebar can render fast lists & let users search without decrypting
// every record. All actual user content (rows / text / suggestions) is sealed.

import Dexie from "dexie";

// ---- AES-GCM key management ------------------------------------------------

const KEY_STORE = "__rtd_secret__";
const KEY_NAME = "data-key-v1";

async function generateKey() {
  return crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    false, // non-extractable — the key never leaves the browser
    ["encrypt", "decrypt"],
  );
}

/**
 * Stores the AES-GCM key inside a dedicated IndexedDB key-store, ringfenced from
 * the data db. `extractable=false` ensures the raw bytes cannot be exfiltrated
 * even by malicious JS running on the same origin.
 */
async function getOrCreateKey() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(KEY_STORE, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore("keys");
    };
    req.onerror = () => reject(req.error);
    req.onsuccess = async () => {
      const db = req.result;
      const tx = db.transaction("keys", "readwrite");
      const store = tx.objectStore("keys");
      const getReq = store.get(KEY_NAME);
      getReq.onsuccess = async () => {
        if (getReq.result) {
          resolve(getReq.result);
          return;
        }
        const k = await generateKey();
        store.put(k, KEY_NAME);
        tx.oncomplete = () => resolve(k);
        tx.onerror = () => reject(tx.error);
      };
      getReq.onerror = () => reject(getReq.error);
    };
  });
}

let _keyPromise = null;
function keyPromise() {
  if (!_keyPromise) _keyPromise = getOrCreateKey();
  return _keyPromise;
}

const enc = new TextEncoder();
const dec = new TextDecoder();

function toB64(bytes) {
  let s = "";
  const arr = new Uint8Array(bytes);
  for (let i = 0; i < arr.byteLength; i += 1) s += String.fromCharCode(arr[i]);
  return btoa(s);
}
function fromB64(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

/** Serialize an arbitrary JSON-able value into an encrypted payload. */
export async function sealJson(value) {
  const key = await keyPromise();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = enc.encode(JSON.stringify(value));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, data);
  return { iv: toB64(iv), ct: toB64(ct), v: 1 };
}

/** Decrypt a sealed payload back into the original JSON value. */
export async function openJson(payload) {
  if (!payload || typeof payload !== "object") return null;
  const key = await keyPromise();
  const iv = new Uint8Array(fromB64(payload.iv));
  const ct = fromB64(payload.ct);
  try {
    const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ct);
    return JSON.parse(dec.decode(pt));
  } catch (err) {
    // If the key was rotated or DB tampered with, return null instead of throwing
    // so the sidebar still renders other items.
    console.warn("openJson: failed to decrypt", err);
    return null;
  }
}

// ---- Dexie schema ----------------------------------------------------------

class RtdLocalDb extends Dexie {
  constructor() {
    super("RegisterToDigital");
    // v1: history (scans + error-scans + searches) keyed by random id
    this.version(1).stores({
      history: "id, kind, ts, title",
    });
  }
}

export const db = new RtdLocalDb();

// ---- Public API ------------------------------------------------------------

/**
 * Save a history item. `kind` is one of "scan" | "errorscan" | "search".
 * `title` and `ts` are plaintext (for sidebar listing); `payload` is sealed.
 */
export async function addHistoryItem({ kind, title, payload }) {
  const id = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random()}`;
  const sealed = await sealJson(payload);
  const item = {
    id,
    kind,
    title: (title || "Untitled").slice(0, 160),
    ts: Date.now(),
    sealed,
  };
  await db.history.put(item);
  return id;
}

export async function listHistory({ kind = null, query = "", limit = 100 } = {}) {
  let coll = db.history.orderBy("ts").reverse();
  if (kind) coll = coll.filter((x) => x.kind === kind);
  if (query) {
    const q = query.toLowerCase();
    coll = coll.filter((x) => (x.title || "").toLowerCase().includes(q));
  }
  return coll.limit(limit).toArray();
}

export async function loadHistoryItem(id) {
  const row = await db.history.get(id);
  if (!row) return null;
  const payload = await openJson(row.sealed);
  return { id: row.id, kind: row.kind, title: row.title, ts: row.ts, payload };
}

export async function deleteHistoryItem(id) {
  return db.history.delete(id);
}

export async function clearAllHistory() {
  return db.history.clear();
}

export async function storageStats() {
  const count = await db.history.count();
  let bytes = 0;
  try {
    if (navigator.storage && navigator.storage.estimate) {
      const est = await navigator.storage.estimate();
      bytes = est.usage || 0;
    }
  } catch {
    // ignore
  }
  return { count, bytes };
}

/** Export the entire local history as plain JSON (already decrypted). */
export async function exportAll() {
  const rows = await db.history.orderBy("ts").reverse().toArray();
  const items = await Promise.all(
    rows.map(async (r) => ({
      id: r.id,
      kind: r.kind,
      title: r.title,
      ts: r.ts,
      payload: await openJson(r.sealed),
    })),
  );
  return {
    app: "RegisterToDigital",
    version: 1,
    exported_at: new Date().toISOString(),
    items,
  };
}

/** Permanently wipe ALL local data (history + encryption key). */
export async function wipeEverything() {
  await db.history.clear();
  // Also drop the key store so the next session generates a fresh key —
  // this guarantees any stale on-disk blobs cannot be decrypted again.
  await new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(KEY_STORE);
    req.onsuccess = req.onerror = req.onblocked = () => resolve();
  });
  _keyPromise = null;
}

export function isLocalEncryptedAvailable() {
  return typeof indexedDB !== "undefined" && !!(crypto && crypto.subtle);
}
